<template>
	<v-card
		variant="outlined"
		class="resource-card-shell"
		data-test="sig-cloud-storage-card"
	>
		<div class="entity-card">
			<!-- TITLE -->
			<div class="entity-card__header">
				<img
					src="@/assets/icons/data-sources/cloud-storage.svg"
					width="32"
					height="32"
					alt="Cloud Storage"
				/>
				<div class="sgnl-flex sgnl-align-center sgnl-paragraph-m-medium sgnl-text-color-default">
					Signaloid Cloud Storage
				</div>
			</div>
			<!-- Usage -->
			<div class="sgnl-flex sgnl-flex-column sgnl-gap-4 sgnl-flex-grow-1">
				<div class="sgnl-flex sgnl-justify-space-between sgnl-align-baseline">
					<div class="sgnl-paragraph-s-medium sgnl-text-color-secondary">Usage</div>
					<div
						v-if="!fetchingUsage"
						class="sgnl-paragraph-s-medium sgnl-text-color-secondary"
					>
						{{ formatUsageString(storageUsage)
						}}<span class="sgnl-paragraph-s-regular sgnl-text-color-secondary"> of </span
						>{{ allowanceText }}
						<template v-if="errorFetchingUsage">
							<v-tooltip open-delay="500">
								<template v-slot:activator="{ props }">
									<Warning16
										class="sgnl-icon-color-warning usage-warning-icon"
										v-bind="props"
									/>
								</template>
								<span
									>There was an error while loading your storage usage. Your data is not affected by
									this error. If this error persists, please contact Signaloid at
									developer-support@signaloid.com.</span
								>
							</v-tooltip>
						</template>
					</div>
				</div>
				<v-progress-linear
					height="7"
					:color="usageBarColour"
					:model-value="usagePercentage"
				/>
			</div>
			<div class="entity-card__actions">
				<v-btn
					variant="flat"
					color="primary"
					@click="showFilesDialog = true"
					data-test="view-files-button"
				>
					<VolumeFileStorage16 />
					<span>View Files</span>
				</v-btn>
				<v-btn
					variant="outlined"
					@click="showFileUploadDialog = true"
					data-test="upload-files-button"
				>
					<CloudUpload16 />
					<span>Upload Files</span>
				</v-btn>
			</div>
		</div>
		<v-dialog
			v-if="showFilesDialog"
			v-model="showFilesDialog"
			@click:outside="showFilesDialog = false"
			@keydown.esc="showFilesDialog = false"
			:width="$vuetify.display.mobile ? '95%' : '500px'"
			scrollable
		>
			<SigCloudStorageFileList
				v-model:showDialog="showFilesDialog"
				@delete-successful="storageDeleteHandler"
				@directory-created="storageDirectoryCreatedHandler"
			/>
		</v-dialog>
		<SigCloudStorageFileUploadDialog
			v-if="showFileUploadDialog"
			v-model:showDialog="showFileUploadDialog"
		/>
	</v-card>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Warning16 from "@carbon/icons-vue/es/warning/16";
import CloudUpload16 from "@carbon/icons-vue/es/cloud--upload/16";
import VolumeFileStorage16 from "@carbon/icons-vue/es/volume--file-storage/16";

import { sdk } from "@/js/sdk";
import SigCloudStorageFileList from "@/components/DataSources/SigCloudStorageFileList.vue";
import SigCloudStorageFileUploadDialog from "@/components/DataSources/SigCloudStorageFileUploadDialog.vue";
import { useUploadsStore } from "@/stores/uploads";

import { UserLimitsE } from "@/js/tierconfig";
import * as dsUtil from "@/components/DataSources/utilities";
import { useUserStore } from "@/stores/user";
import { useRootStore } from "@/stores/root";

import { TierLimitEventTypeE } from "@/eventBus/tierLimitEventBus";
import { monitoringCaptureError } from "@/plugins/monitoring";

type ComponentData = {
	driveData: undefined;
	storageUsage: undefined | number;
	storageAllowance: undefined | null | number;
	showFilesDialog: boolean;
	showFileUploadDialog: boolean;
	fetchingUsage: boolean;
	errorFetchingUsage: boolean;
};

export default defineComponent({
	name: "SigCloudStorageCard",
	components: {
		SigCloudStorageFileList,
		SigCloudStorageFileUploadDialog,
		Warning16,
		CloudUpload16,
		VolumeFileStorage16,
	},
	props: {},
	emits: {
		"storage-operation-successful": (message: string) => typeof message === "string",
	},
	data: (): ComponentData => ({
		driveData: undefined,
		storageUsage: undefined,
		storageAllowance: undefined,
		showFilesDialog: false,
		showFileUploadDialog: false,
		fetchingUsage: false,
		errorFetchingUsage: false,
	}),
	setup() {
		const userStore = useUserStore();
		const rootStore = useRootStore();
		const uploadsStore = useUploadsStore();
		return { userStore, rootStore, uploadsStore, dataSourceIcon: dsUtil.dataSourceIcon };
	},
	watch: {
		"uploadsStore.completionTick"() {
			// silent: keep the usage figure visible while it refreshes.
			this.fetchUsage(true);
		},
	},
	computed: {
		usageBarColour() {
			if (this.usagePercentage >= 90) {
				return "danger";
			} else if (this.usagePercentage >= 80) {
				return "warning";
			}
			return "var(--sgnl-brand-background)";
		},
		usagePercentage(): number {
			return this.storageUsage && this.storageAllowance ? (100 * this.storageUsage) / this.storageAllowance : 0;
		},
		allowanceText(): string {
			if (this.storageAllowance !== undefined) {
				return this.formatUsageString(this.storageAllowance ?? 0);
			}

			return this.errorFetchingUsage ? this.formatUsageString(undefined) : "Unlimited";
		},
	},
	methods: {
		formatUsageString(usage: undefined | number) {
			if (usage !== undefined) {
				if (usage >= 1e9) {
					return `${(usage / 1e9).toFixed(2)} GB`;
				} else if (usage >= 1e6) {
					return `${(usage / 1e6).toFixed(2)} MB`;
				} else if (usage >= 1e3) {
					return `${(usage / 1e3).toFixed(2)} KB`;
				} else {
					return `${usage} B`;
				}
			} else {
				return "-- MB";
			}
		},

		async fetchUsage(silent = false) {
			this.rootStore.tierLimitEventBus.emit({
				type: TierLimitEventTypeE.UsageChanged,
				affectedLimits: [UserLimitsE.CloudStorageBytes],
			});
			try {
				if (!silent) {
					this.fetchingUsage = true;
				}
				// get user usage
				const userID = await this.userStore.getCurrentUserID();
				const userDetails = await sdk.users.getOne(userID);
				this.storageUsage =
					userDetails?.ResourceUsage?.CloudStorageBytes ??
					// @ts-ignore this is a legacy key that we should remove
					userDetails?.ResourceUsage?.DataStorage ??
					undefined;

				const tierDetails = await this.userStore.getCurrentUserTierDetails();
				this.storageAllowance = tierDetails?.allowance.CloudStorageBytes;
				this.errorFetchingUsage = this.storageUsage === undefined || tierDetails === undefined;
			} catch (error) {
				this.errorFetchingUsage = true;
				monitoringCaptureError(error, "Fetch user quota usage");
			} finally {
				this.fetchingUsage = false;
			}
		},

		storageDeleteHandler(itemType: string) {
			this.fetchUsage();
			const message = itemType === "directory" ? "Directory deleted successfully." : "File deleted successfully.";
			this.$emit("storage-operation-successful", message);
		},
		storageDirectoryCreatedHandler() {
			this.fetchUsage();
			this.$emit("storage-operation-successful", "Directory created successfully.");
		},
	},
	mounted() {
		this.fetchUsage();
	},
});
</script>

<style scoped>
.usage-warning-icon {
	vertical-align: middle;
}
</style>
